// import React from 'react'

// const AdminInvoices = () => {
//   return (
//     <div>AdminInvoices</div>
//   )
// }

// export default AdminInvoices

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminInvoices,
  selectAllInvoices,
  selectTotalInvoices,
  selectInvoiceLoading,
  selectInvoiceError,
  selectPagination,
  selectTotalAmount,
  selectPaidAmount,
  selectPendingAmount,
  setFilters,
  setPagination,
  clearFilters,
  clearError,
} from "../redux/slices/adminInvoicesSlice";

// MUI Imports
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";

const AdminInvoices = () => {
  const dispatch = useDispatch();
  const invoices = useSelector(selectAllInvoices);
  const totalInvoices = useSelector(selectTotalInvoices);
  const loading = useSelector(selectInvoiceLoading);
  const error = useSelector(selectInvoiceError);
  const pagination = useSelector(selectPagination);
  const totalAmount = useSelector(selectTotalAmount);
  const paidAmount = useSelector(selectPaidAmount);
  const pendingAmount = useSelector(selectPendingAmount);

  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch invoices on mount and when filters/pagination change
  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };

    if (statusFilter) params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;

    dispatch(fetchAdminInvoices(params));
  }, [dispatch, page, rowsPerPage, statusFilter, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (statusFilter) params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;
    dispatch(fetchAdminInvoices(params));
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setPage(0);
    dispatch(clearFilters());
    dispatch(fetchAdminInvoices({ page: 1, limit: rowsPerPage }));
  };

  const getStatusChip = (status) => {
    const statusMap = {
      PAID: { color: "success", label: "Paid" },
      PENDING: { color: "warning", label: "Pending" },
      PAYMENT_SUBMITTED: { color: "info", label: "Payment Submitted" },
      EXPIRED: { color: "error", label: "Expired" },
    };
    const config = statusMap[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading && invoices.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Invoice Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Invoices
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                {totalInvoices}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                ₹{totalAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Paid Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                ₹{paidAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                ₹{pendingAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PAYMENT_SUBMITTED">Payment Submitted</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by invoice # or restaurant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="textSecondary" align="right">
              Total: <strong>{totalInvoices}</strong> invoices
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Restaurant</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {invoice.restaurantId?.restaurantName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {invoice.restaurantId?.mobile || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {invoice.restaurantId?.ownerName || "N/A"}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">₹{invoice.amount}</Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(invoice.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(invoice.dueDate)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Grace: {formatDate(invoice.graceEndDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="textSecondary">
                      No invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalInvoices}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count}`
          }
        />
      </Paper>
    </Container>
  );
};

export default AdminInvoices;